import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Upload } from 'lucide-react';
import {
  MONTHS_PER_YEAR,
  budgetQuarter,
  budgetSourceText,
  formatCurrency,
  formatMonthName,
  formatMonthShort,
  formatNumber,
  parseNumber,
  splitOverYear,
  type BudgetEntryInput,
  type BudgetResponse,
  type BudgetSheetRow,
} from '@codepass/shared';
import { ApiError, api, apiUrl } from '@/lib/api';
import { useAuth } from '@/auth/AuthProvider';
import { cn } from '@/lib/cn';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Combobox,
  Input,
  Label,
  Select,
  Skeleton,
  Table,
  Td,
  Th,
  Tr,
} from '@/components/ui';
import { PageHeader } from '@/components/domain';

/**
 * /budget - budgettet pr. konto og maaned, i kontoplanen fra Business Central som saldobalancen i
 * docs/saldobalance.xlsx. Det gemmes i Codepass, fordi BC's budget ikke kan skrives til - se
 * apps/api/src/domain/budget.ts. Indtaegter er positive, omkostninger negative.
 *
 * Et beloeb saettes paa én af to maader: paa én maaned, der saa baerer hele beloebet, eller paa
 * aaret, hvor det deles paa de tolv maaneder (`splitOverYear`). Begge erstatter det, maanederne
 * havde. Hele aaret kan ogsaa hentes og laegges op som Excel - filen er aarets budget.
 *
 * TV-skaermene og /dashboard maaler omsaetningen mod budgettet her for en maaned med omsaetning
 * paa, og mod BC's budget ellers - kortet "Mål og budget" oeverst viser det, med kvartaler og
 * maaneder.
 */

type Message = { tone: 'info' | 'error'; text: string };

/** 'year' spreads the amount over the twelve months; '1'-'12' puts it all in that month. */
type Spread = 'year' | `${number}`;

function monthIso(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}-01`;
}

function errorText(cause: unknown, fallback: string): string {
  return cause instanceof ApiError ? cause.message : fallback;
}

/** The file as base64, without the `data:...;base64,` in front. */
function readBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ''));
    reader.onerror = () => reject(reader.error ?? new Error('Kunne ikke læse filen.'));
    reader.readAsDataURL(file);
  });
}

export function BudgetPage() {
  const queryClient = useQueryClient();
  const canEdit = useAuth().can('budget.manage');
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [showAll, setShowAll] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);

  const [accountNo, setAccountNo] = useState('');
  const [amountText, setAmountText] = useState('');
  const [spread, setSpread] = useState<Spread>('year');
  const fileInput = useRef<HTMLInputElement>(null);

  const query = useQuery({
    queryKey: ['budget', year],
    queryFn: () => api.get<BudgetResponse>('/budget', { year }),
  });

  const saved = (result: BudgetResponse, text: string): void => {
    queryClient.setQueryData(['budget', result.year], result);
    setMessage({ tone: 'info', text });
  };

  const save = useMutation({
    mutationFn: (input: BudgetEntryInput) => api.put<BudgetResponse>('/budget/entries', input),
    onMutate: () => setMessage(null),
    onSuccess: (result, input) => {
      const account = result.rows.find((row) => row.no === input.account_no);
      const where =
        input.month === null
          ? `fordelt på årets ${MONTHS_PER_YEAR} måneder`
          : `i ${formatMonthName(monthIso(input.year, input.month))} ${input.year}`;
      saved(
        result,
        `${input.account_no} ${account?.name ?? ''}: ${formatCurrency(input.amount)} ${where}.`,
      );
      setAmountText('');
    },
    onError: (cause) =>
      setMessage({ tone: 'error', text: errorText(cause, 'Kunne ikke gemme budgettet.') }),
  });

  const upload = useMutation({
    mutationFn: async (file: File) =>
      api.post<BudgetResponse & { imported_accounts: number }>('/budget/import', {
        year,
        file_base64: await readBase64(file),
      }),
    onMutate: () => setMessage(null),
    onSuccess: (result, file) =>
      saved(
        result,
        `${file.name} er lagt op som budget for ${result.year}: ${result.imported_accounts} ` +
          `${result.imported_accounts === 1 ? 'konto' : 'konti'} med beløb. Konti, filen ikke har, er sat til 0.`,
      ),
    onError: (cause) =>
      setMessage({ tone: 'error', text: errorText(cause, 'Kunne ikke lægge filen op.') }),
  });

  const data = query.data;
  const amount = parseNumber(amountText);
  const valid = accountNo !== '' && amountText.trim() !== '' && Number.isFinite(amount);
  const accounts = (data?.rows ?? []).filter((row) => row.type === 'posting');
  const hasBudget = accounts.some((row) => !row.empty);
  const visible =
    showAll || !hasBudget ? (data?.rows ?? []) : (data?.rows ?? []).filter((row) => !row.empty);
  const years = [thisYear - 1, thisYear, thisYear + 1, thisYear + 2];

  return (
    <>
      <PageHeader
        title="Budget"
        description="Budgettet pr. konto og måned, i kontoplanen fra Business Central. Indtægter er positive og omkostninger negative, som i saldobalancen. Beløb er ekskl. moms."
        actions={
          <>
            <Select
              aria-label="År"
              className="w-28"
              value={year}
              onChange={(event) => {
                setYear(Number(event.target.value));
                setMessage(null);
              }}
            >
              {years.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Select>
            <a href={apiUrl('/budget/export', { year })}>
              <Button size="sm" variant="secondary">
                <Download className="h-3.5 w-3.5" aria-hidden />
                Download Excel
              </Button>
            </a>
            {canEdit ? (
              <>
                <input
                  ref={fileInput}
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    if (file) upload.mutate(file);
                  }}
                />
                <Button
                  size="sm"
                  variant="secondary"
                  loading={upload.isPending}
                  disabled={!data?.ready}
                  onClick={() => fileInput.current?.click()}
                >
                  {upload.isPending ? null : <Upload className="h-3.5 w-3.5" aria-hidden />}
                  Upload Excel
                </Button>
              </>
            ) : null}
          </>
        }
      />

      {message ? (
        <Alert tone={message.tone} className="mb-4" onDismiss={() => setMessage(null)}>
          {message.text}
        </Alert>
      ) : null}

      {query.isLoading ? (
        <Skeleton className="h-96" />
      ) : query.error || !data ? (
        <Alert tone="error">{errorText(query.error, 'Kunne ikke hente budgettet.')}</Alert>
      ) : (
        <div className="space-y-4">
          {!data.ready ? (
            <Alert tone="warning" title="Budgettabellen er ikke sat op">
              Kør SQL’en i docs/budget-opsaetning.md i Supabase’s SQL-editor. Indtil da kan intet
              gemmes.
            </Alert>
          ) : null}

          <TargetCard data={data} />

          {canEdit ? (
            <Card>
              <CardHeader>
                <CardTitle>Sæt et beløb</CardTitle>
              </CardHeader>
              <CardBody>
                <form
                  className="grid gap-4 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1.3fr)_auto] md:items-end"
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (!valid) return;
                    save.mutate({
                      year,
                      account_no: accountNo,
                      amount,
                      month: spread === 'year' ? null : Number(spread),
                    });
                  }}
                >
                  <div className="space-y-1.5">
                    <Label>Konto</Label>
                    <Combobox
                      value={accountNo}
                      onChange={setAccountNo}
                      allLabel="Vælg konto"
                      searchPlaceholder="Søg på nummer eller navn"
                      options={accounts.map((row) => ({
                        value: row.no,
                        label: `${row.no} ${row.name}`,
                      }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="budget-amount">Beløb</Label>
                    <Input
                      id="budget-amount"
                      inputMode="decimal"
                      placeholder="fx 120.000 eller -5.000"
                      value={amountText}
                      onChange={(event) => setAmountText(event.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="budget-spread">Fordeling</Label>
                    <Select
                      id="budget-spread"
                      value={spread}
                      onChange={(event) => setSpread(event.target.value as Spread)}
                    >
                      <option value="year">Hele året — deles på 12 måneder</option>
                      {Array.from({ length: MONTHS_PER_YEAR }, (_, index) => (
                        <option key={index} value={String(index + 1)}>
                          Kun {formatMonthName(monthIso(year, index + 1))}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <Button type="submit" disabled={!valid || !data.ready} loading={save.isPending}>
                    Gem
                  </Button>
                  <p className="text-xs text-ink-muted md:col-span-4">
                    {valid
                      ? spreadHint(amount, spread, year)
                      : 'Beløbet erstatter det, måneden eller året havde på kontoen. 0 rydder det.'}
                  </p>
                </form>
              </CardBody>
            </Card>
          ) : null}

          <Card>
            <CardHeader className="flex flex-wrap items-center justify-between gap-3">
              <CardTitle>Resultatopgørelse {year}</CardTitle>
              {hasBudget ? (
                <label className="flex items-center gap-2 text-xs text-ink-muted">
                  <input
                    type="checkbox"
                    checked={showAll}
                    onChange={(event) => setShowAll(event.target.checked)}
                  />
                  Vis også konti uden budget
                </label>
              ) : null}
            </CardHeader>
            <BudgetTable rows={visible} year={year} />
          </Card>
        </div>
      )}
    </>
  );
}

/**
 * Maal og budget - the view that was on /admin/tv-dashboard until 26-10-2026: the revenue the
 * screens measure the year against, by quarter and month. Quarters are summed in packages/shared
 * (calc/budget.ts), so the page and the screen agree.
 */
function TargetCard({ data }: { data: BudgetResponse }) {
  const { year, target } = data;
  /* Months from both - then each BC month is marked. */
  const mixed = new Set(target?.month_sources).size > 1;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Mål og budget</CardTitle>
        <p className="mt-0.5 text-xs text-ink-muted">
          Omsætningsbudgettet, som måned, kvartal og år på TV-skærmene og /dashboard måler mod, og
          som flytter den hvide streg: stregen er, hvor budgettet forventer, at vi er i dag. En
          måned med omsætning her bruger budgettet her; de andre måneder bruger budgettet i Business
          Central. Kun konti med kategorien Indtægt i kontoplanen tæller som omsætning. Beløb er
          ekskl. moms.
        </p>
      </CardHeader>
      <CardBody className="space-y-4">
        {data.problem ? <Alert tone="warning">{data.problem}</Alert> : null}

        {target ? (
          <>
            <div className="flex flex-wrap items-end gap-6">
              <div>
                <p className="text-xs text-ink-muted">Årsmål {year}</p>
                <p className="tabular text-lg font-semibold">
                  {formatCurrency(target.target_excl_vat)}
                </p>
              </div>
              <p className="max-w-md text-xs text-ink-muted">
                Fra {budgetSourceText(target.budget_name, target.month_sources)},{' '}
                {target.accounts.length === 1 ? 'konto' : 'konti'} {target.accounts.join(', ')}.
                Kvartalerne er summen af deres måneder.
                {mixed ? ' Måneder fra Business Central er mærket BC.' : null}
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {[1, 2, 3, 4].map((quarter) => (
                <div key={quarter} className="space-y-1.5 rounded-md border border-line p-3">
                  <p className="text-sm font-semibold">
                    Q{quarter}
                    <span className="tabular ml-2 font-normal text-ink-muted">
                      {formatCurrency(budgetQuarter(target.months, quarter))}
                    </span>
                  </p>
                  {[0, 1, 2].map((offset) => {
                    const index = (quarter - 1) * 3 + offset;
                    return (
                      <div key={index} className="flex items-center justify-between gap-2 text-sm">
                        <span className="text-ink-muted">
                          {formatMonthName(monthIso(year, index + 1))}
                          {mixed && target.month_sources[index] === 'bc' ? (
                            <Badge className="ml-1.5 px-1.5 py-0 text-[10px]">BC</Badge>
                          ) : null}
                        </span>
                        <span className="tabular">{formatCurrency(target.months[index] ?? 0)}</span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </>
        ) : data.problem ? null : (
          <p className="text-sm text-ink-muted">
            Der er intet budget for {year} — hverken her eller i Business Central. Skærmene skriver
            &quot;Intet mål sat&quot;, indtil der er et.
          </p>
        )}
      </CardBody>
    </Card>
  );
}

/** What the form will do, in a sentence - the split shown before it is saved. */
function spreadHint(amount: number, spread: Spread, year: number): string {
  if (spread !== 'year') {
    return `${formatCurrency(amount)} i ${formatMonthName(monthIso(year, Number(spread)))}. De andre måneder røres ikke.`;
  }
  const months = splitOverYear(amount);
  const first = months[0] ?? 0;
  const last = months[MONTHS_PER_YEAR - 1] ?? 0;
  return first === last
    ? `${MONTHS_PER_YEAR} × ${formatCurrency(first)}.`
    : `${MONTHS_PER_YEAR - 1} × ${formatCurrency(first)} og ${formatCurrency(last)} i december, så året giver præcis ${formatCurrency(amount)}.`;
}

function BudgetTable({ rows, year }: { rows: readonly BudgetSheetRow[]; year: number }) {
  return (
    <Table className="text-xs">
      <thead>
        <tr>
          <Th>Nr.</Th>
          <Th>Navn</Th>
          {Array.from({ length: MONTHS_PER_YEAR }, (_, index) => (
            <Th key={index} className="text-right">
              {formatMonthShort(monthIso(year, index + 1))}
            </Th>
          ))}
          <Th className="text-right">I alt</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const strong = row.type !== 'posting';
          return (
            <Tr
              key={row.no}
              className={cn(strong && 'font-semibold', row.type === 'total' && 'bg-canvas')}
            >
              <Td className="tabular text-ink-muted">{row.no}</Td>
              <Td className="min-w-56">{row.name}</Td>
              {(row.months ?? Array<null>(MONTHS_PER_YEAR).fill(null)).map((value, index) => (
                <Td key={index} className="tabular whitespace-nowrap text-right">
                  {value === null || (value === 0 && !strong) ? '' : formatNumber(value)}
                </Td>
              ))}
              <Td className="tabular whitespace-nowrap text-right">
                {row.total === null ? '' : formatNumber(row.total)}
              </Td>
            </Tr>
          );
        })}
      </tbody>
    </Table>
  );
}
